import { Box, Card, Typography } from '@mui/material';
import {
    Bar,
    BarChart,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import dayjs from 'dayjs';
import { t } from 'i18next';

const formatDate = (ts) => dayjs(ts).format('DD/MM HH:mm');

const getDynamicDomain = (chart, key = 'value', bufferPercent = 0.05) => {
    if (!chart || chart.length === 0) return ['auto', 'auto'];

    let values;
    if (key === 'systolic' || key === 'diastolic') {
        values = chart.flatMap((d) => [d.systolic, d.diastolic]);
    } else {
        values = chart.map((d) => d[key]);
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    return [min - range * bufferPercent, max + range * bufferPercent];
};

const hasData = (chart, chartType) => {
    if (!chart || chart.length === 0) return false;

    if (chartType === 'dual-line') {
        return chart.some((d) => d.systolic || d.diastolic);
    }

    return chart.some((d) => d.value);
};

export default function StatCard({
    label,
    unit,
    icon,
    current,
    currentDate,
    previous,
    previousDate,
    chart,
    chartType
}) {
    const yAxisDomain = getDynamicDomain(chart, chartType === 'dual-line' ? '' : 'value');
    const dataAvailable = hasData(chart, chartType);

    return (
        <Card
            elevation={3}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: 2,
                borderRadius: 3,
                height: 220
            }}
        >
            <Box display="flex" alignItems="center" mb={1}>
                {icon}
                <Typography variant="h6" ml={1}>
                    {label}
                </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" fontWeight={800} color="#1976d2">
                    {current} {unit}
                </Typography>
                {currentDate && (
                    <Typography variant="caption" color="text.secondary">
                        {dayjs(currentDate).format('DD/MM/YYYY')}
                    </Typography>
                )}
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    {t('userPage.previousValue', { previous, unit })}
                </Typography>
                {previousDate && (
                    <Typography variant="caption" color="text.secondary">
                        {dayjs(previousDate).format('DD/MM/YYYY')}
                    </Typography>
                )}
            </Box>

            <Box
                flexGrow={1}
                mt={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                {dataAvailable ? (
                    <ResponsiveContainer width="100%" height="100%">
                        {chartType === 'line' && (
                            <LineChart data={chart}>
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#1976d2"
                                    strokeWidth={2}
                                    dot={{ r: 2 }}
                                />
                                <Tooltip
                                    labelFormatter={(v) => formatDate(v)}
                                    formatter={(val) => [`${val} ${unit}`, label]}
                                />
                                <XAxis dataKey="ts" hide />
                                <YAxis hide domain={yAxisDomain} />
                            </LineChart>
                        )}
                        {chartType === 'bar' && (
                            <BarChart data={chart}>
                                <Bar
                                    dataKey="value"
                                    fill="#1976d2"
                                    radius={[4, 4, 0, 0]}
                                />
                                <Tooltip
                                    labelFormatter={(v) => formatDate(v)}
                                    formatter={(val) => [`${val} ${unit}`, label]}
                                />
                                <XAxis dataKey="ts" hide />
                                <YAxis hide domain={yAxisDomain} />
                            </BarChart>
                        )}
                        {chartType === 'dual-line' && (
                            <LineChart data={chart}>
                                <Line
                                    type="monotone"
                                    dataKey="systolic"
                                    stroke="#d32f2f"
                                    strokeWidth={2}
                                    dot={{ r: 2 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="diastolic"
                                    stroke="#1976d2"
                                    strokeWidth={2}
                                    dot={{ r: 2 }}
                                />
                                <Tooltip
                                    labelFormatter={(v) => formatDate(v)}
                                    formatter={(val, name) => [
                                        `${val} ${unit}`,
                                        name === 'systolic'
                                            ? t('userPage.systolic')
                                            : t('userPage.diastolic')
                                    ]}
                                />
                                <XAxis dataKey="ts" hide />
                                <YAxis hide domain={yAxisDomain} />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {t('userPage.noRecords')}
                    </Typography>
                )}
            </Box>
        </Card>
    );
}
